import { Copy, Download, Share2, X } from "lucide-react";
import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { createBrandedShareImage, dataUrlFile } from "./utils/shareImage";
import "./share.css";

export interface SharePayload {
  title: string;
  text: string;
  url: string;
  image?: string;
  kicker: string;
  filename: string;
  source?: "map" | "victory" | "hoard" | "creator" | "chain";
}

function openShareUrl(url: string) {
  window.open(url, "_blank", "noopener,noreferrer,width=720,height=720");
}

function reportShare(stage: string, source = "unknown", error?: unknown) {
  window.dispatchEvent(new CustomEvent("rw:share-event", { detail: {
    stage,
    source,
    error: error instanceof Error ? error.message : error ? String(error) : "",
  } }));
}

async function leaveFullscreen() {
  const page = document as Document & {
    webkitFullscreenElement?: Element | null;
    webkitExitFullscreen?: () => Promise<void> | void;
  };
  try {
    if (page.fullscreenElement && page.exitFullscreen) await page.exitFullscreen();
    else if (page.webkitFullscreenElement && page.webkitExitFullscreen) await page.webkitExitFullscreen();
  } catch {
    // The share dialog still works when a browser refuses programmatic fullscreen exit.
  }
}

async function copyText(value: string) {
  try {
    await navigator.clipboard.writeText(value);
    return true;
  } catch {
    const input = document.createElement("textarea");
    input.value = value;
    input.readOnly = true;
    Object.assign(input.style, { position: "fixed", left: "-9999px", opacity: "0" });
    document.body.appendChild(input);
    input.select();
    let copied = false;
    try { copied = document.execCommand("copy"); } catch { copied = false; }
    input.remove();
    return copied;
  }
}

export function ShareDialog({
  payload,
  onClose,
}: {
  payload: SharePayload | null;
  onClose: () => void;
}) {
  const [image, setImage] = useState<string>("");
  const [shareFile, setShareFile] = useState<File | null>(null);
  const [status, setStatus] = useState("");
  const dialogRef = useRef<HTMLDialogElement>(null);
  const isOpen = payload !== null;
  useLayoutEffect(() => {
    const dialog = dialogRef.current;
    if (!isOpen || !dialog) return;
    try {
      if (!dialog.open) dialog.showModal();
    } catch {
      // Older embedded browsers may not implement showModal, but still render
      // an open dialog as a fixed overlay.
      dialog.setAttribute("open", "");
    }
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previousOverflow;
      if (dialog.open) dialog.close();
    };
  }, [isOpen]);
  useEffect(() => {
    let active = true;
    if (!payload) return () => { active = false; };
    void leaveFullscreen();
    reportShare("opened", payload.source);
    setImage("");
    setShareFile(null);
    setStatus(payload ? "PREPARING SHARE IMAGE…" : "");
    void createBrandedShareImage({
        source: payload.image,
        title: payload.title,
        kicker: payload.kicker,
      })
        .then((result) => {
          if (active) {
            setImage(result);
            setStatus("");
          }
          return dataUrlFile(result, payload.filename);
        })
        .then((file) => {
          if (active) setShareFile(file);
        })
        .catch((error) => {
          reportShare("image_failed", payload.source, error);
          if (active) setStatus("THE LINK IS READY · IMAGE PREVIEW UNAVAILABLE");
        });
    return () => {
      active = false;
    };
  }, [payload]);
  if (!payload) return null;

  const shareText = `${payload.text} ${payload.url}`;
  const nativeShare = async () => {
    if (!navigator.share) {
      const copied = await copyText(shareText);
      reportShare(copied ? "fallback_copied" : "fallback_failed", payload.source);
      setStatus(copied ? "SHARE TEXT COPIED" : "SHARING IS UNAVAILABLE · USE THE DIRECT LINK BELOW");
      return;
    }
    try {
      const data: ShareData = { title: payload.title, text: payload.text, url: payload.url };
      if (shareFile && navigator.canShare?.({ files: [shareFile] })) data.files = [shareFile];
      await navigator.share(data);
      reportShare("native_complete", payload.source);
      setStatus("SHARED");
    } catch (error) {
      if (error instanceof DOMException && error.name === "AbortError") return;
      reportShare("native_failed", payload.source, error);
      const copied = await copyText(shareText);
      setStatus(copied ? "SHARE SHEET FAILED · SHARE TEXT COPIED" : "SHARE SHEET FAILED · USE A PLATFORM BUTTON");
    }
  };
  const copyLink = async () => {
    const copied = await copyText(payload.url);
    reportShare(copied ? "link_copied" : "copy_failed", payload.source);
    setStatus(copied ? "DIRECT LINK COPIED" : "COPY BLOCKED · PRESS AND HOLD THE LINK BELOW");
  };
  const download = () => {
    if (!image) return;
    const anchor = document.createElement("a");
    anchor.href = image;
    anchor.download = payload.filename;
    anchor.rel = "noopener";
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    reportShare("image_downloaded", payload.source);
    setStatus("BRANDED IMAGE DOWNLOADED");
  };
  const handoffVisualPlatform = (platform: "instagram" | "tiktok") => {
    openShareUrl(platform === "instagram" ? "https://www.instagram.com/" : "https://www.tiktok.com/upload");
    if (image) download();
    void copyText(shareText).then((copied) => {
      reportShare(`${platform}_${copied ? "opened" : "copy_failed"}`, payload.source);
      setStatus(`${image ? "IMAGE READY" : "LINK READY"} · ${copied ? "CAPTION COPIED" : "COPY CAPTION MANUALLY"} FOR ${platform.toUpperCase()}`);
    });
  };

  return createPortal(
    <dialog ref={dialogRef} className="share-backdrop" aria-label="Share MatterMaze" onCancel={(event) => { event.preventDefault(); onClose(); }} onClick={(event) => { if (event.target === event.currentTarget) onClose(); }}>
      <section className="share-dialog pixel-panel" onClick={(event) => event.stopPropagation()}>
        <header><div><small>SHARE FROM MATTERMAZE</small><h2>{payload.title}</h2></div><button type="button" onClick={onClose} aria-label="Close share dialog"><X size={18} /></button></header>
        <div className="share-preview">{image ? <img src={image} alt="Branded MatterMaze share preview" /> : <span>{status}</span>}</div>
        <div className="share-platforms">
          <button type="button" className="native" onClick={() => void nativeShare()}><Share2 size={17} /> SHARE…</button>
          <button type="button" onClick={() => openShareUrl(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(payload.url)}`)}>f FACEBOOK</button>
          <button type="button" onClick={() => openShareUrl(`https://twitter.com/intent/tweet?text=${encodeURIComponent(payload.text)}&url=${encodeURIComponent(payload.url)}`)}>X</button>
          <button type="button" onClick={() => openShareUrl(`https://wa.me/?text=${encodeURIComponent(shareText)}`)}>WHATSAPP</button>
          <button type="button" onClick={() => handoffVisualPlatform("instagram")}>INSTAGRAM</button>
          <button type="button" onClick={() => handoffVisualPlatform("tiktok")}>TIKTOK</button>
        </div>
        <div className="share-tools"><button type="button" onClick={() => void copyLink()}><Copy size={15} /> COPY DIRECT LINK</button><button type="button" disabled={!image} onClick={download}><Download size={15} /> DOWNLOAD PNG</button></div>
        <a className="share-direct-link" href={payload.url}>{payload.url}</a>
        <p>{status || "Instagram and TikTok use the downloaded branded image and copied caption; supported phones can use Share… directly."}</p>
      </section>
    </dialog>,
    document.body,
  );
}
